import { create } from 'zustand';
import { temporal } from 'zundo';
import { useStore } from 'zustand';
import { useCallback } from 'react';

import { api } from '@/lib/api';
import {
  createColumn,
  createElement,
  createQuizSlide,
  createRow,
  createSlide,
  mintBeatId,
} from '@/lib/factories';
import { beatIdOf, findInsertionEdge, findPointersTo, slideIdFor } from '@/lib/routing';
import type {
  BeatMeta,
  Column,
  ElementType,
  LessonPackOp,
  RevealStep,
  Row,
  RowLayout,
  Script,
  ScriptElement,
  ScriptMetadata,
  ScriptOp,
  Slide,
  SlideKind,
} from '@/lib/schema.types';
import { debounce, uid } from '@/lib/utils';

/**
 * Which backend resource the sync engine targets.
 * - `script` → PATCH /scripts/:id/ops (with PUT /scripts/:id replace fallback)
 * - `pack`   → PATCH /lesson-packs/:id/ops (no replace fallback; backend has none)
 */
export type EditorMode = 'script' | 'pack';

export type Selection =
  | { kind: 'slide'; slideId: string }
  | { kind: 'row'; slideId: string; rowId: string }
  | { kind: 'column'; slideId: string; rowId: string; columnId: string }
  | {
      kind: 'element';
      slideId: string;
      rowId: string;
      columnId: string;
      elementId: string;
    }
  | null;

export type SyncStatus = 'idle' | 'pending' | 'saving' | 'error';

export interface ClipboardItem {
  kind: 'element';
  element: ScriptElement;
}

/** Extra pack-level metadata the store needs for validation + routing UIs.
 *  Set at hydrate time from the LessonPack envelope. Empty in script mode. */
export interface PackMeta {
  entryBeatId?: string | null;
  publishStateAtLoad?: 'draft' | 'published';
}

interface EditorState {
  script: Script | null;
  /** Which backend resource ops should be POSTed against. */
  mode: EditorMode;
  /** Pack-level fields outside `script.slides[]` (entryBeatId, etc.). */
  packMeta: PackMeta;
  selection: Selection;
  syncStatus: SyncStatus;
  syncError?: string;
  clipboard: ClipboardItem | null;
  /** Slide currently open in the Teach Studio modal — null when closed. */
  teachingSlideId: string | null;

  // hydration
  hydrate(script: Script, mode?: EditorMode, packMeta?: PackMeta): void;
  reset(): void;
  /**
   * Same as `reset()` but flushes any pending sync ops first, awaiting the
   * network round-trip before clearing the engine's `scriptId` / queue.
   * Use this in page-unmount cleanups so the last 600ms of edits aren't
   * silently dropped if the user navigates away mid-debounce.
   */
  drainAndReset(): Promise<void>;
  setTeachingSlideId(slideId: string | null): void;

  // selection
  setSelection(sel: Selection): void;

  // metadata
  updateMeta(patch: { label?: string; metadata?: Partial<ScriptMetadata> }): void;

  // slides
  addSlide(index?: number, opts?: { kind?: SlideKind; label?: string }): string;

  insertQuizSlide(opts?: { afterSlideId?: string; label?: string }): string | null;
  removeSlide(slideId: string): void;
  reorderSlides(slideIds: string[]): void;
  updateSlide(slideId: string, patch: Partial<Slide>): void;

  insertSlide(slide: Slide, index?: number): void;

  // rows
  addRow(slideId: string, layout?: RowLayout, index?: number): string;
  insertRow(slideId: string, row: Row, index?: number): void;
  removeRow(slideId: string, rowId: string): void;
  reorderRows(slideId: string, rowIds: string[]): void;
  moveRow(fromSlideId: string, toSlideId: string, rowId: string, toIndex: number): void;
  updateRow(slideId: string, rowId: string, patch: Partial<Row>): void;

  // columns
  updateColumn(slideId: string, rowId: string, columnId: string, patch: Partial<Column>): void;

  // elements
  addElement(
    slideId: string,
    rowId: string,
    columnId: string,
    type: ElementType,
    index?: number,
  ): string;
  insertElement(
    slideId: string,
    rowId: string,
    columnId: string,
    element: ScriptElement,
    index?: number,
  ): void;
  removeElement(slideId: string, rowId: string, columnId: string, elementId: string): void;
  updateElement(
    slideId: string,
    rowId: string,
    columnId: string,
    elementId: string,
    patch: Partial<ScriptElement>,
  ): void;
  moveElement(
    from: { slideId: string; rowId: string; columnId: string },
    to: { slideId: string; rowId: string; columnId: string },
    elementId: string,
    toIndex: number,
  ): void;

  // freeform grouping + layer ordering (authoring helpers; sync via updateElement)
  groupFreeformElements(slideId: string, rowId: string, columnId: string, elementIds: string[]): void;
  ungroupFreeformElements(slideId: string, rowId: string, columnId: string, groupId: string): void;
  reorderFreeformLayers(
    slideId: string,
    rowId: string,
    columnId: string,
    orderedElementIds: string[],
  ): void;

  // reveal steps
  upsertRevealStep(slideId: string, step: RevealStep, index?: number): void;
  removeRevealStep(slideId: string, stepId: string): void;
  reorderRevealSteps(slideId: string, stepIds: string[]): void;

  // beat-meta ops (pack mode only — no-op safety guards keep these inert
  // in script mode, but the actions should not be wired into script UI).
  updateBeatMeta(slideId: string, patch: Partial<BeatMeta>): void;
  updateRouting(
    slideId: string,
    patch: Partial<NonNullable<BeatMeta['next']>>,
  ): void;
  updateQuizBranch(
    slideId: string,
    optionId: string,
    patch: Record<string, unknown>,
  ): void;

  // higher-level actions
  duplicateSelection(): void;
  deleteSelection(): void;
  copySelection(): void;
  pasteSelection(): void;

  // sync
  enqueueOp(op: LessonPackOp): void;
  flushSync(): Promise<void>;
  setSyncStatus(status: SyncStatus, err?: string): void;
}

// ---------- helpers ----------

function findSlide(script: Script, slideId: string): Slide | undefined {
  return script.slides.find((s) => s.id === slideId);
}
function findRow(script: Script, slideId: string, rowId: string): Row | undefined {
  return findSlide(script, slideId)?.rows.find((r) => r.id === rowId);
}
function findColumn(
  script: Script,
  slideId: string,
  rowId: string,
  columnId: string,
): Column | undefined {
  return findRow(script, slideId, rowId)?.columns.find((c) => c.id === columnId);
}
function findElement(
  script: Script,
  slideId: string,
  rowId: string,
  columnId: string,
  elementId: string,
): ScriptElement | undefined {
  return findColumn(script, slideId, rowId, columnId)?.elements.find((e) => e.id === elementId);
}

/** Collect every existing beat id in the pack so newly minted ids can be
 *  collision-checked. Empty set in script mode (no beatMeta). */
function collectBeatIds(script: Script | null): Set<string> {
  const out = new Set<string>();
  if (!script) return out;
  for (const s of script.slides) {
    const b = beatIdOf(s);
    if (b) out.add(b);
  }
  return out;
}

/**
 * For pack-mode `removeSlide`: given the slide being removed and the set of
 * pointers found by `findPointersTo`, return:
 *   - `ops`: the rewire ops to batch BEFORE `removeSlide` (per
 *     SCHEMA_USAGE.md Rule 2)
 *   - `localPatches`: a `{slideId → newBeatMeta}` map so the caller can
 *     mirror the changes in the in-memory store before set()
 *
 * Every pointer is redirected to `redirectTarget` — the doomed slide's own
 * `next.default` ("skip past me"), or `null` to terminate that path.
 *
 * Grouping per affected slide:
 *   - All `next.*` hits collapse into ONE `updateRouting` op.
 *   - All other hits (adaptiveTriggers + pace/confidence on*) require the
 *     full surrounding object preserved (the backend's `updateBeatMeta`
 *     merge is shallow), so they collapse into ONE `updateBeatMeta` op per
 *     slide carrying the rebuilt top-level fields.
 */
function buildRemovalRewires(
  slides: Slide[],
  doomedBeatId: string,
  redirectTarget: string | null,
): {
  ops: LessonPackOp[];
  localPatches: Map<string, BeatMeta>;
} {
  const hits = findPointersTo(slides, doomedBeatId);
  const ops: LessonPackOp[] = [];
  const localPatches = new Map<string, BeatMeta>();

  // Group hits by slide.
  const bySlide = new Map<string, typeof hits>();
  for (const h of hits) {
    const arr = bySlide.get(h.slideId) ?? [];
    arr.push(h);
    bySlide.set(h.slideId, arr);
  }

  for (const [slideId, slideHits] of bySlide) {
    const slide = slides.find((s) => s.id === slideId);
    if (!slide || !slide.beatMeta) continue;

    // Start with a fresh copy we mutate as we apply hits, then write back.
    const nextBM: BeatMeta = JSON.parse(JSON.stringify(slide.beatMeta));

    const nextRoutingPatch: Record<string, string | null> = {};
    let beatMetaPatchTouched = false;

    for (const h of slideHits) {
      if (h.location.startsWith('next.')) {
        const key = h.location.slice('next.'.length); // default | ifExpanded | …
        nextRoutingPatch[key] = redirectTarget;
        nextBM.next = { ...(nextBM.next ?? {}), [key]: redirectTarget };
        continue;
      }

      if (h.location.startsWith('adaptiveTriggers.')) {
        const triggerKey = h.location.split('.')[1]; // lowEngagement | …
        const triggers = (nextBM.adaptiveTriggers as
          | Record<string, { targetBeatId?: string | null }>
          | undefined) ?? {};
        triggers[triggerKey] = {
          ...(triggers[triggerKey] ?? {}),
          targetBeatId: redirectTarget,
        };
        nextBM.adaptiveTriggers = triggers;
        beatMetaPatchTouched = true;
        continue;
      }

      // pace_check / confidence_check on* keys: top-level beatMeta field
      // with a `targetBeatId`. The brief location format is
      // `<key>.targetBeatId` (e.g. `onMajorityYes.targetBeatId`).
      const topKey = h.location.split('.')[0];
      const existing = (nextBM as unknown as Record<string, { targetBeatId?: string | null }>)[
        topKey
      ] ?? {};
      (nextBM as unknown as Record<string, unknown>)[topKey] = {
        ...existing,
        targetBeatId: redirectTarget,
      };
      beatMetaPatchTouched = true;
    }

    if (Object.keys(nextRoutingPatch).length > 0) {
      ops.push({
        op: 'updateRouting',
        payload: { slideId, patch: nextRoutingPatch },
      });
    }

    if (beatMetaPatchTouched) {
      // Send the rebuilt top-level fields the backend's shallow merge needs:
      // adaptiveTriggers + every changed on* key.
      const patch: Partial<BeatMeta> = {};
      const changedTopKeys = new Set(
        slideHits
          .filter((h) => !h.location.startsWith('next.'))
          .map((h) =>
            h.location.startsWith('adaptiveTriggers.')
              ? 'adaptiveTriggers'
              : h.location.split('.')[0],
          ),
      );
      for (const k of changedTopKeys) {
        (patch as Record<string, unknown>)[k] = (nextBM as Record<string, unknown>)[k];
      }
      ops.push({ op: 'updateBeatMeta', payload: { slideId, patch } });
    }

    localPatches.set(slideId, nextBM);
  }

  return { ops, localPatches };
}

// ---------- sync engine (singleton outside store) ----------

interface SyncEngine {
  /**
   * Queued ops typed as the wider `LessonPackOp` (a superset of `ScriptOp`
   * that also covers the 3 beat-meta ops). In script mode only `ScriptOp`
   * values ever land here — the beat-meta actions are pack-only — so the
   * narrowing cast at the script dispatch boundary is safe.
   */
  queue: LessonPackOp[];
  scriptId: string | null;
  mode: EditorMode;
  enqueue(op: LessonPackOp): void;
  flush(): Promise<void>;
}

function createSyncEngine(): SyncEngine {
  const engine: SyncEngine = {
    queue: [],
    scriptId: null,
    mode: 'script',
    enqueue(op) {
      engine.queue.push(op);
      useEditorStore.getState().setSyncStatus('pending');
      debouncedFlush();
    },
    async flush() {
      if (!engine.scriptId || engine.queue.length === 0) return;
      const ops = engine.queue.splice(0, engine.queue.length);
      useEditorStore.getState().setSyncStatus('saving');
      try {
        if (engine.mode === 'pack') {
          await api.lessonPacks.applyOps(engine.scriptId, ops);
        } else {
          // Beat-meta ops can't arrive here in script mode because the
          // actions that emit them are pack-only; cast to satisfy the
          // narrower scripts endpoint.
          await api.scripts.applyOps(engine.scriptId, ops as ScriptOp[]);
        }
        useEditorStore.getState().setSyncStatus('idle');
      } catch (err) {
        // Scripts have a full-replace fallback (PUT /scripts/:id). Lesson
        // packs don't — surface the error and let the user retry.
        if (engine.mode === 'script') {
          const script = useEditorStore.getState().script;
          if (script) {
            try {
              await api.scripts.replace(script.id, {
                version: script.version,
                label: script.label,
                metadata: script.metadata,
                slides: script.slides,
              });
              useEditorStore.getState().setSyncStatus('idle');
              return;
            } catch (err2) {
              useEditorStore
                .getState()
                .setSyncStatus('error', err2 instanceof Error ? err2.message : 'sync failed');
              return;
            }
          }
        }
        useEditorStore
          .getState()
          .setSyncStatus('error', err instanceof Error ? err.message : 'sync failed');
      }
    },
  };
  const debouncedFlush = debounce(() => {
    void engine.flush();
  }, 600);
  return engine;
}

const sync = createSyncEngine();

// ---------- store ----------

export const useEditorStore = create<EditorState>()(
  temporal(
    (set, get) => ({
      script: null,
      mode: 'script',
      packMeta: {},
      selection: null,
      syncStatus: 'idle',
      clipboard: null,
      teachingSlideId: null,

      hydrate(script, mode = 'script', packMeta = {}) {
        sync.scriptId = script.id;
        sync.mode = mode;
        sync.queue.length = 0;

        // Self-heal a dangling entry: if `entryBeatId` names a slide that's no
        // longer present (e.g. the entry slide was deleted by an older build,
        // before the delete-fix), retarget it to the first slide and persist
        // via `updateEntryBeat` — otherwise the pack is permanently un-startable.
        let healedPackMeta = packMeta;
        let healOp: { op: 'updateEntryBeat'; payload: { entryBeatId: string | null } } | null =
          null;
        if (mode === 'pack' && packMeta.entryBeatId) {
          const beatIds = new Set(
            script.slides.map((s) => beatIdOf(s)).filter(Boolean) as string[],
          );
          if (!beatIds.has(packMeta.entryBeatId)) {
            const newEntry = beatIdOf(script.slides[0]) ?? null;
            healedPackMeta = { ...packMeta, entryBeatId: newEntry };
            healOp = { op: 'updateEntryBeat', payload: { entryBeatId: newEntry } };
          }
        }

        set({
          script,
          mode,
          packMeta: healedPackMeta,
          selection: null,
          syncStatus: 'idle',
          syncError: undefined,
          teachingSlideId: null,
        });

        if (healOp) get().enqueueOp(healOp);
      },
      reset() {
        sync.scriptId = null;
        sync.mode = 'script';
        sync.queue.length = 0;
        set({
          script: null,
          mode: 'script',
          packMeta: {},
          selection: null,
          syncStatus: 'idle',
          syncError: undefined,
          teachingSlideId: null,
        });
      },
      async drainAndReset() {
        // The flush reads `engine.scriptId` at the top, so as long as we
        // await it BEFORE clearing the engine, in-flight ops land on the
        // right pack. The debounce timer can still fire later with an empty
        // queue — that's a harmless no-op.
        try {
          await sync.flush();
        } catch {
          /* surface via syncStatus; don't block teardown */
        }
        sync.scriptId = null;
        sync.mode = 'script';
        sync.queue.length = 0;
        set({
          script: null,
          mode: 'script',
          packMeta: {},
          selection: null,
          syncStatus: 'idle',
          syncError: undefined,
          teachingSlideId: null,
        });
      },
      setTeachingSlideId(teachingSlideId) {
        set({ teachingSlideId });
      },

      setSelection: (selection) => set({ selection }),

      updateMeta(patch) {
        const script = get().script;
        if (!script) return;
        const next: Script = {
          ...script,
          label: patch.label ?? script.label,
          metadata: { ...script.metadata, ...(patch.metadata ?? {}) },
        };
        set({ script: next });
        // Same nested-replace hazard as updateElement: send the FULLY
        // merged `metadata` object on the wire so a partial caller doesn't
        // wipe siblings server-side.
        get().enqueueOp({
          op: 'updateMeta',
          payload: {
            label: patch.label,
            metadata: patch.metadata ? next.metadata : undefined,
          },
        });
      },

      addSlide(index, opts) {
        const mode = get().mode;
        if (mode === 'pack') {
          // Pack mode mints a proper beatMeta-bearing slide so the routing
          // rewire downstream can run. `mintBeatId` is imported above; we
          // reuse it implicitly via createSlide({ kind }).
          const existing = collectBeatIds(get().script);
          const slide = createSlide({
            kind: opts?.kind ?? 'teach',
            label: opts?.label,
            existingBeatIds: existing,
          });
          get().insertSlide(slide, index);
          return slide.id;
        }
        const slide = createSlide({ label: opts?.label });
        get().insertSlide(slide, index);
        return slide.id;
      },

      insertQuizSlide(opts) {
        if (get().mode !== 'pack') return null;
        const script = get().script;
        if (!script) return null;

        // Resolve "after which slide?" — explicit afterSlideId wins,
        // otherwise current selection's slide, otherwise append at end.
        const sel = get().selection;
        const anchorSlideId =
          opts?.afterSlideId ??
          (sel && 'slideId' in sel ? sel.slideId : null) ??
          script.slides[script.slides.length - 1]?.id ??
          null;

        const anchorIdx = anchorSlideId
          ? script.slides.findIndex((s) => s.id === anchorSlideId)
          : -1;
        const insertIdx = anchorIdx >= 0 ? anchorIdx + 1 : script.slides.length;

        const slide = createQuizSlide({
          label: opts?.label,
          existingBeatIds: collectBeatIds(script),
        });
        get().insertSlide(slide, insertIdx);
        return slide.id;
      },

      insertSlide(slide, index) {
        const script = get().script;
        if (!script) return;
        const idx = typeof index === 'number' ? index : script.slides.length;
        const mode = get().mode;

        // Pack-mode rewire: if the new slide carries beatMeta, splice into
        // the routing graph at the same time we splice into the array. See
        // SCHEMA_USAGE.md Rule 1 ("the 11th-slide bug").
        let routedSlide = slide;
        let predecessorOp: LessonPackOp | null = null;
        if (mode === 'pack' && slide.beatMeta) {
          const { predecessor, successorBeatId } = findInsertionEdge(
            script.slides,
            idx,
          );
          // The new slide takes the successor's place in the chain.
          routedSlide = {
            ...slide,
            beatMeta: {
              ...slide.beatMeta,
              next: {
                ...(slide.beatMeta.next ?? {}),
                default: successorBeatId,
              },
            },
          };
          if (predecessor && predecessor.beatMeta) {
            const newBeatId = beatIdOf(routedSlide);
            if (newBeatId) {
              // Rewire predecessor's default routing to the new slide. If any
              // of ifExpanded/ifReteach/ifSkipped pointed at the same
              // successor, rewire those too so alt paths route through the
              // insertion (per SCHEMA_USAGE.md Rule 1 "non-default routing
              // variants in flight").
              const patch: Record<string, string> = { default: newBeatId };
              const oldNext = predecessor.beatMeta.next;
              if (oldNext) {
                if (oldNext.ifExpanded === successorBeatId) patch.ifExpanded = newBeatId;
                if (oldNext.ifReteach === successorBeatId) patch.ifReteach = newBeatId;
                if (oldNext.ifSkipped === successorBeatId) patch.ifSkipped = newBeatId;
              }
              predecessorOp = {
                op: 'updateRouting',
                payload: { slideId: predecessor.id, patch },
              };
            }
          } else if (mode === 'pack') {
            // No predecessor with beatMeta — pack is malformed (no beatMeta
            // graph yet) or we're inserting before the entry beat. Either
            // way, the new slide is orphaned; flag it for pre-save validation.
            // eslint-disable-next-line no-console
            console.warn(
              '[editor] inserted pack slide without a routing predecessor — pack may be orphaned',
              { newSlideId: slide.id, arrayIndex: idx },
            );
          }
        }

        const slides = [...script.slides];
        slides.splice(idx, 0, routedSlide);

        // Mirror the predecessor's local routing patch into our in-memory
        // copy so the UI reflects the rewire immediately (the enqueued ops
        // will flush to the backend in the next debounce tick).
        const nextSlides = predecessorOp
          ? slides.map((s) => {
              if (s.id !== predecessorOp.payload.slideId) return s;
              const oldBM = s.beatMeta ?? {};
              return {
                ...s,
                beatMeta: {
                  ...oldBM,
                  next: { ...(oldBM.next ?? {}), ...predecessorOp.payload.patch },
                },
              };
            })
          : slides;

        set({
          script: { ...script, slides: nextSlides },
          selection: { kind: 'slide', slideId: routedSlide.id },
        });
        get().enqueueOp({ op: 'insertSlide', payload: { slide: routedSlide, index: idx } });
        if (predecessorOp) get().enqueueOp(predecessorOp);
      },

      removeSlide(slideId) {
        const script = get().script;
        if (!script) return;
        if (script.slides.length <= 1) return; // never delete last slide
        const mode = get().mode;
        const doomed = script.slides.find((s) => s.id === slideId);
        const sel = get().selection;
        const nextSel = sel && 'slideId' in sel && sel.slideId === slideId ? null : sel;

        if (mode === 'pack' && doomed?.beatMeta) {
          const doomedBeatId = beatIdOf(doomed);
          if (doomedBeatId) {
            // "Skip past me" — anything that pointed at the doomed slide
            // gets redirected to wherever the doomed slide was going next.
            // If the doomed slide had no `next.default`, the path
            // terminates (`null`). Guard against self-loops just in case.
            let redirectTarget = doomed.beatMeta.next?.default ?? null;
            if (redirectTarget === doomedBeatId) redirectTarget = null;

            const { ops: rewireOps, localPatches } = buildRemovalRewires(
              script.slides,
              doomedBeatId,
              redirectTarget,
            );

            // Mirror the rewires + the removal in local state.
            const mirroredSlides = script.slides
              .filter((s) => s.id !== slideId)
              .map((s) => {
                const patched = localPatches.get(s.id);
                return patched ? { ...s, beatMeta: patched } : s;
              });

            // If we're deleting the lesson's ENTRY slide, retarget entryBeatId
            // to where it routed next (redirectTarget), else the new first
            // slide — otherwise the pack is left with a dangling entry and
            // Electron can't start the lesson. Persisted via the pack-level
            // `updateEntryBeat` op (entryBeatId lives outside slides[]).
            const prevEntry = get().packMeta.entryBeatId ?? null;
            const removingEntry = prevEntry != null && prevEntry === doomedBeatId;
            const newEntry = removingEntry
              ? (redirectTarget ?? beatIdOf(mirroredSlides[0]) ?? null)
              : prevEntry;

            set((st) => ({
              script: { ...script, slides: mirroredSlides },
              selection: nextSel,
              packMeta: removingEntry
                ? { ...st.packMeta, entryBeatId: newEntry }
                : st.packMeta,
            }));

            // Emit rewire ops first, then the removal — all in one PATCH
            // batch via the debounced flush. (SCHEMA_USAGE.md Rule 2.)
            for (const op of rewireOps) get().enqueueOp(op);
            get().enqueueOp({ op: 'removeSlide', payload: { slideId } });
            if (removingEntry) {
              get().enqueueOp({ op: 'updateEntryBeat', payload: { entryBeatId: newEntry } });
            }
            return;
          }
        }

        // Script mode (or pack with no beatMeta) — original behavior.
        const filtered = script.slides.filter((s) => s.id !== slideId);
        set({ script: { ...script, slides: filtered }, selection: nextSel });
        get().enqueueOp({ op: 'removeSlide', payload: { slideId } });
      },

      reorderSlides(slideIds) {
        const script = get().script;
        if (!script) return;
        const map = new Map(script.slides.map((s) => [s.id, s]));
        const slides = slideIds.map((id) => map.get(id)!).filter(Boolean);
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'reorderSlides', payload: { slideIds } });
      },

      updateSlide(slideId, patch) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => (s.id === slideId ? { ...s, ...patch } : s));
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'updateSlide', payload: { slideId, patch } });
      },

      addRow(slideId, layout = 1, index) {
        const row = createRow(layout);
        get().insertRow(slideId, row, index);
        return row.id;
      },

      insertRow(slideId, row, index) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const idx = typeof index === 'number' ? index : s.rows.length;
          const rows = [...s.rows];
          rows.splice(idx, 0, row);
          return { ...s, rows };
        });
        set({
          script: { ...script, slides },
          selection: { kind: 'row', slideId, rowId: row.id },
        });
        get().enqueueOp({ op: 'insertRow', payload: { slideId, row, index } });
      },

      removeRow(slideId, rowId) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) =>
          s.id === slideId ? { ...s, rows: s.rows.filter((r) => r.id !== rowId) } : s,
        );
        set({ script: { ...script, slides }, selection: null });
        get().enqueueOp({ op: 'removeRow', payload: { slideId, rowId } });
      },

      reorderRows(slideId, rowIds) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const map = new Map(s.rows.map((r) => [r.id, r]));
          const rows = rowIds.map((id) => map.get(id)!).filter(Boolean);
          return { ...s, rows };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'reorderRows', payload: { slideId, rowIds } });
      },

      moveRow(fromSlideId, toSlideId, rowId, toIndex) {
        const script = get().script;
        if (!script) return;
        let movedRow: Row | undefined;
        const after1 = script.slides.map((s) => {
          if (s.id !== fromSlideId) return s;
          const rows = s.rows.filter((r) => {
            if (r.id === rowId) {
              movedRow = r;
              return false;
            }
            return true;
          });
          return { ...s, rows };
        });
        if (!movedRow) return;
        const slides = after1.map((s) => {
          if (s.id !== toSlideId) return s;
          const rows = [...s.rows];
          const idx = Math.min(Math.max(toIndex, 0), rows.length);
          rows.splice(idx, 0, movedRow!);
          return { ...s, rows };
        });
        set({
          script: { ...script, slides },
          selection: { kind: 'row', slideId: toSlideId, rowId },
        });
        get().enqueueOp({
          op: 'moveRow',
          payload: { fromSlideId, toSlideId, rowId, toIndex },
        });
      },

      updateRow(slideId, rowId, patch) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const rows = s.rows.map((r) => {
            if (r.id !== rowId) return r;
            const next: Row = { ...r, ...patch };
            // re-balance columns when layout changes
            if (patch.layout !== undefined && patch.layout !== r.layout) {
              const targetCount = patch.layout === 0 ? 1 : patch.layout;
              const widthPercent = Math.floor(100 / targetCount);
              const existing = r.columns;
              const columns: Column[] = [];
              for (let i = 0; i < targetCount; i++) {
                if (existing[i]) columns.push({ ...existing[i], widthPercent });
                else columns.push(createColumn(widthPercent));
              }
              next.columns = columns;
            }
            return next;
          });
          return { ...s, rows };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'updateRow', payload: { slideId, rowId, patch } });
      },

      updateColumn(slideId, rowId, columnId, patch) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const rows = s.rows.map((r) => {
            if (r.id !== rowId) return r;
            const columns = r.columns.map((c) => (c.id === columnId ? { ...c, ...patch } : c));
            return { ...r, columns };
          });
          return { ...s, rows };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'updateColumn', payload: { slideId, rowId, columnId, patch } });
      },

      addElement(slideId, rowId, columnId, type, index) {
        const element = createElement(type);
        get().insertElement(slideId, rowId, columnId, element, index);
        return element.id;
      },

      insertElement(slideId, rowId, columnId, element, index) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const rows = s.rows.map((r) => {
            if (r.id !== rowId) return r;
            const columns = r.columns.map((c) => {
              if (c.id !== columnId) return c;
              const idx = typeof index === 'number' ? index : c.elements.length;
              const elements = [...c.elements];
              elements.splice(idx, 0, element);
              return { ...c, elements };
            });
            return { ...r, columns };
          });
          return { ...s, rows };
        });
        set({
          script: { ...script, slides },
          selection: { kind: 'element', slideId, rowId, columnId, elementId: element.id },
        });
        get().enqueueOp({
          op: 'insertElement',
          payload: { slideId, rowId, columnId, element, index },
        });
      },

      removeElement(slideId, rowId, columnId, elementId) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const rows = s.rows.map((r) => {
            if (r.id !== rowId) return r;
            const columns = r.columns.map((c) => {
              if (c.id !== columnId) return c;
              return { ...c, elements: c.elements.filter((e) => e.id !== elementId) };
            });
            return { ...r, columns };
          });
          return { ...s, rows };
        });
        set({ script: { ...script, slides }, selection: null });
        get().enqueueOp({
          op: 'removeElement',
          payload: { slideId, rowId, columnId, elementId },
        });
      },

      updateElement(slideId, rowId, columnId, elementId, patch) {
        const script = get().script;
        if (!script) return;

        // Backend's `updateElement` op does `Object.assign(element, patch)`
        // — a SHALLOW merge that REPLACES nested objects entirely. If we
        // forward a delta like `{ style: { fontSize: 40 } }` on the wire,
        // any previously-set style key (color, fontFamily, textAlign…) gets
        // wiped on the next reload, even though the local UI looked right.
        // Fix: keep the local shallow-merge as before, but build the wire
        // patch with FULL merged `style` / `position` objects so the
        // server-side replace is harmless. (Same hazard applies to row /
        // column / slide updates below.)
        const existing = findElement(script, slideId, rowId, columnId, elementId);

        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const rows = s.rows.map((r) => {
            if (r.id !== rowId) return r;
            const columns = r.columns.map((c) => {
              if (c.id !== columnId) return c;
              const elements = c.elements.map((e) =>
                e.id === elementId
                  ? {
                      ...e,
                      ...patch,
                      style: patch.style ? { ...e.style, ...patch.style } : e.style,
                      position: patch.position
                        ? { ...e.position, ...patch.position }
                        : e.position,
                    }
                  : e,
              );
              return { ...c, elements };
            });
            return { ...r, columns };
          });
          return { ...s, rows };
        });
        set({ script: { ...script, slides } });

        const wirePatch: Partial<ScriptElement> = { ...patch };
        if (patch.style && existing) {
          wirePatch.style = { ...existing.style, ...patch.style };
        }
        if (patch.position && existing) {
          wirePatch.position = { ...existing.position, ...patch.position };
        }
        get().enqueueOp({
          op: 'updateElement',
          payload: { slideId, rowId, columnId, elementId, patch: wirePatch },
        });
      },

      moveElement(from, to, elementId, toIndex) {
        const script = get().script;
        if (!script) return;
        const fromCol = findColumn(script, from.slideId, from.rowId, from.columnId);
        if (!fromCol) return;
        const element = fromCol.elements.find((e) => e.id === elementId);
        if (!element) return;

        const slides = script.slides.map((s) => {
          let next = s;
          if (s.id === from.slideId) {
            next = {
              ...next,
              rows: next.rows.map((r) =>
                r.id === from.rowId
                  ? {
                      ...r,
                      columns: r.columns.map((c) =>
                        c.id === from.columnId
                          ? { ...c, elements: c.elements.filter((e) => e.id !== elementId) }
                          : c,
                      ),
                    }
                  : r,
              ),
            };
          }
          if (next.id === to.slideId) {
            next = {
              ...next,
              rows: next.rows.map((r) =>
                r.id === to.rowId
                  ? {
                      ...r,
                      columns: r.columns.map((c) => {
                        if (c.id !== to.columnId) return c;
                        const elements = [...c.elements];
                        const idx = Math.min(Math.max(toIndex, 0), elements.length);
                        elements.splice(idx, 0, element);
                        return { ...c, elements };
                      }),
                    }
                  : r,
              ),
            };
          }
          return next;
        });

        set({
          script: { ...script, slides },
          selection: { kind: 'element', ...to, elementId },
        });
        get().enqueueOp({ op: 'moveElement', payload: { from, to, elementId, toIndex } });
      },

      groupFreeformElements(slideId, rowId, columnId, elementIds) {
        if (elementIds.length < 2) return;
        const groupId = uid('grp');
        // Reuse updateElement so the shallow-merge-safe wire patch is built for
        // us. `groupId` is a top-level scalar, so the patch is safe as-is.
        for (const elementId of elementIds) {
          get().updateElement(slideId, rowId, columnId, elementId, { groupId });
        }
      },

      ungroupFreeformElements(slideId, rowId, columnId, groupId) {
        const col = findColumn(get().script as Script, slideId, rowId, columnId);
        if (!col) return;
        const members = col.elements.filter((e) => e.groupId === groupId).map((e) => e.id);
        for (const elementId of members) {
          get().updateElement(slideId, rowId, columnId, elementId, { groupId: undefined });
        }
      },

      reorderFreeformLayers(slideId, rowId, columnId, orderedElementIds) {
        const n = orderedElementIds.length;
        if (n === 0) return;
        // Top of the list paints last → highest zIndex. updateElement merges the
        // full `position` object on the wire, so we only pass the changed key.
        orderedElementIds.forEach((elementId, i) => {
          get().updateElement(slideId, rowId, columnId, elementId, {
            position: { zIndex: n - i },
          });
        });
      },

      upsertRevealStep(slideId, step, index) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const existing = s.revealSteps ?? [];
          const i = existing.findIndex((x) => x.id === step.id);
          let next: RevealStep[];
          if (i >= 0) {
            next = existing.map((x) => (x.id === step.id ? step : x));
          } else {
            next = [...existing];
            const idx = typeof index === 'number' ? index : next.length;
            next.splice(idx, 0, step);
          }
          return { ...s, revealSteps: next };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'upsertRevealStep', payload: { slideId, step, index } });
      },

      removeRevealStep(slideId, stepId) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) =>
          s.id === slideId
            ? { ...s, revealSteps: (s.revealSteps ?? []).filter((x) => x.id !== stepId) }
            : s,
        );
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'removeRevealStep', payload: { slideId, stepId } });
      },

      reorderRevealSteps(slideId, stepIds) {
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const map = new Map((s.revealSteps ?? []).map((x) => [x.id, x]));
          const revealSteps = stepIds.map((id) => map.get(id)!).filter(Boolean);
          return { ...s, revealSteps };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'reorderRevealSteps', payload: { slideId, stepIds } });
      },

      // ---- beat-meta ops (pack mode) ----
      // These shallow-merge into `slide.beatMeta` / `beatMeta.next` /
      // `beatMeta.branches[optionId]` to match the backend's merge semantics
      // (see backend INTEGRATION.md §4.2 and SCHEMA_USAGE.md Zone 3). They're
      // no-ops outside pack mode so accidental wiring stays inert.
      updateBeatMeta(slideId, patch) {
        if (get().mode !== 'pack') return;
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) =>
          s.id === slideId
            ? { ...s, beatMeta: { ...(s.beatMeta ?? {}), ...patch } }
            : s,
        );
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'updateBeatMeta', payload: { slideId, patch } });
      },

      updateRouting(slideId, patch) {
        if (get().mode !== 'pack') return;
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const beatMeta = { ...(s.beatMeta ?? {}) };
          beatMeta.next = { ...(beatMeta.next ?? {}), ...patch };
          return { ...s, beatMeta };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({ op: 'updateRouting', payload: { slideId, patch } });
      },

      updateQuizBranch(slideId, optionId, patch) {
        if (get().mode !== 'pack') return;
        const script = get().script;
        if (!script) return;
        const slides = script.slides.map((s) => {
          if (s.id !== slideId) return s;
          const beatMeta = { ...(s.beatMeta ?? {}) };
          const branches = { ...(beatMeta.branches ?? {}) };
          branches[optionId] = { ...(branches[optionId] ?? {}), ...patch };
          beatMeta.branches = branches;
          return { ...s, beatMeta };
        });
        set({ script: { ...script, slides } });
        get().enqueueOp({
          op: 'updateQuizBranch',
          payload: { slideId, optionId, patch },
        });
      },

      duplicateSelection() {
        const { script, selection } = get();
        if (!script || !selection) return;
        if (selection.kind === 'element') {
          const el = findElement(
            script,
            selection.slideId,
            selection.rowId,
            selection.columnId,
            selection.elementId,
          );
          if (!el) return;
          const clone: ScriptElement = JSON.parse(JSON.stringify(el));
          clone.id = uid('el');
          get().insertElement(selection.slideId, selection.rowId, selection.columnId, clone);
        } else if (selection.kind === 'row') {
          const row = findRow(script, selection.slideId, selection.rowId);
          if (!row) return;
          const clone: Row = JSON.parse(JSON.stringify(row));
          clone.id = uid('r');
          clone.columns = clone.columns.map((c) => ({
            ...c,
            id: uid('c'),
            elements: c.elements.map((e) => ({ ...e, id: uid('el') })),
          }));
          const slide = findSlide(script, selection.slideId);
          if (!slide) return;
          const idx = slide.rows.findIndex((r) => r.id === row.id) + 1;
          get().insertRow(selection.slideId, clone, idx);
        } else if (selection.kind === 'slide') {
          const slide = findSlide(script, selection.slideId);
          if (!slide) return;
          const clone: Slide = JSON.parse(JSON.stringify(slide));
          clone.label = `${slide.label} (copy)`;
          clone.rows = clone.rows.map((r) => ({
            ...r,
            id: uid('r'),
            columns: r.columns.map((c) => ({
              ...c,
              id: uid('c'),
              elements: c.elements.map((e) => ({ ...e, id: uid('el') })),
            })),
          }));

          // Pack mode: the clone must get a fresh beat id so the routing
          // graph stays unique. We also clear the clone's outgoing
          // `next.default` — `insertSlide` will set it to whatever sits at
          // the insertion edge in the chain.
          if (get().mode === 'pack' && clone.beatMeta) {
            const kind = (clone.beatMeta.kind as SlideKind) ?? 'teach';
            const newBeatId = mintBeatId(kind, collectBeatIds(script));
            clone.id = slideIdFor(newBeatId);
            clone.beatMeta = {
              ...clone.beatMeta,
              beatId: newBeatId,
              slideId: clone.id,
              next: {
                ...(clone.beatMeta.next ?? {}),
                default: undefined,
                ifExpanded: undefined,
                ifReteach: undefined,
                ifSkipped: undefined,
              },
            };
          } else {
            clone.id = uid('slide');
          }

          const idx = script.slides.findIndex((s) => s.id === slide.id) + 1;
          get().insertSlide(clone, idx);
        }
      },

      deleteSelection() {
        const sel = get().selection;
        if (!sel) return;
        if (sel.kind === 'element')
          get().removeElement(sel.slideId, sel.rowId, sel.columnId, sel.elementId);
        else if (sel.kind === 'row') get().removeRow(sel.slideId, sel.rowId);
        else if (sel.kind === 'slide') get().removeSlide(sel.slideId);
      },

      copySelection() {
        const { script, selection } = get();
        if (!script || !selection || selection.kind !== 'element') return;
        const el = findElement(
          script,
          selection.slideId,
          selection.rowId,
          selection.columnId,
          selection.elementId,
        );
        if (!el) return;
        set({ clipboard: { kind: 'element', element: JSON.parse(JSON.stringify(el)) } });
      },

      pasteSelection() {
        const { script, selection, clipboard } = get();
        if (!script || !clipboard) return;
        if (clipboard.kind !== 'element') return;
        const target =
          selection && (selection.kind === 'element' || selection.kind === 'column')
            ? selection
            : null;
        if (!target) return;
        const clone: ScriptElement = JSON.parse(JSON.stringify(clipboard.element));
        clone.id = uid('el');
        get().insertElement(target.slideId, target.rowId, target.columnId, clone);
      },

      enqueueOp(op) {
        sync.enqueue(op);
      },

      async flushSync() {
        await sync.flush();
      },

      setSyncStatus(syncStatus, syncError) {
        set({ syncStatus, syncError });
      },
    }),
    {
      // Track only the script + selection in history; sync + teach state is transient.
      partialize: (state) => ({ script: state.script, selection: state.selection }),
      // (the partialize already excludes teachingSlideId)
      limit: 100,
      equality: (a, b) => a.script === b.script,
    },
  ),
);

// ---------- ergonomic hooks ----------

export const useScript = () => useEditorStore((s) => s.script);
export const useSelection = () => useEditorStore((s) => s.selection);
export const useSyncStatus = () =>
  useEditorStore((s) => ({ status: s.syncStatus, error: s.syncError }));

export function useEditorActions() {
  const store = useEditorStore;
  return {
    setSelection: useCallback((s: Selection) => store.getState().setSelection(s), [store]),
    updateMeta: useCallback(
      (p: { label?: string; metadata?: Partial<ScriptMetadata> }) => store.getState().updateMeta(p),
      [store],
    ),
    addSlide: useCallback(
      (idx?: number, opts?: { kind?: SlideKind; label?: string }) =>
        store.getState().addSlide(idx, opts),
      [store],
    ),
    insertQuizSlide: useCallback(
      (opts?: { afterSlideId?: string; label?: string }) =>
        store.getState().insertQuizSlide(opts),
      [store],
    ),
    insertSlide: useCallback(
      (slide: Slide, idx?: number) => store.getState().insertSlide(slide, idx),
      [store],
    ),
    removeSlide: useCallback((id: string) => store.getState().removeSlide(id), [store]),
    reorderSlides: useCallback((ids: string[]) => store.getState().reorderSlides(ids), [store]),
    updateSlide: useCallback(
      (id: string, patch: Partial<Slide>) => store.getState().updateSlide(id, patch),
      [store],
    ),
    addRow: useCallback(
      (slideId: string, layout: RowLayout = 1, index?: number) =>
        store.getState().addRow(slideId, layout, index),
      [store],
    ),
    insertRow: useCallback(
      (slideId: string, row: Row, index?: number) => store.getState().insertRow(slideId, row, index),
      [store],
    ),
    removeRow: useCallback(
      (slideId: string, rowId: string) => store.getState().removeRow(slideId, rowId),
      [store],
    ),
    reorderRows: useCallback(
      (slideId: string, rowIds: string[]) => store.getState().reorderRows(slideId, rowIds),
      [store],
    ),
    moveRow: useCallback(
      (fromSlideId: string, toSlideId: string, rowId: string, toIndex: number) =>
        store.getState().moveRow(fromSlideId, toSlideId, rowId, toIndex),
      [store],
    ),
    updateRow: useCallback(
      (slideId: string, rowId: string, patch: Partial<Row>) =>
        store.getState().updateRow(slideId, rowId, patch),
      [store],
    ),
    updateColumn: useCallback(
      (slideId: string, rowId: string, columnId: string, patch: Partial<Column>) =>
        store.getState().updateColumn(slideId, rowId, columnId, patch),
      [store],
    ),
    addElement: useCallback(
      (slideId: string, rowId: string, columnId: string, type: ElementType, index?: number) =>
        store.getState().addElement(slideId, rowId, columnId, type, index),
      [store],
    ),
    insertElement: useCallback(
      (
        slideId: string,
        rowId: string,
        columnId: string,
        element: ScriptElement,
        index?: number,
      ) => store.getState().insertElement(slideId, rowId, columnId, element, index),
      [store],
    ),
    updateElement: useCallback(
      (
        slideId: string,
        rowId: string,
        columnId: string,
        elementId: string,
        patch: Partial<ScriptElement>,
      ) => store.getState().updateElement(slideId, rowId, columnId, elementId, patch),
      [store],
    ),
    removeElement: useCallback(
      (slideId: string, rowId: string, columnId: string, elementId: string) =>
        store.getState().removeElement(slideId, rowId, columnId, elementId),
      [store],
    ),
    moveElement: useCallback(
      (
        from: { slideId: string; rowId: string; columnId: string },
        to: { slideId: string; rowId: string; columnId: string },
        elementId: string,
        toIndex: number,
      ) => store.getState().moveElement(from, to, elementId, toIndex),
      [store],
    ),
    groupFreeformElements: useCallback(
      (slideId: string, rowId: string, columnId: string, elementIds: string[]) =>
        store.getState().groupFreeformElements(slideId, rowId, columnId, elementIds),
      [store],
    ),
    ungroupFreeformElements: useCallback(
      (slideId: string, rowId: string, columnId: string, groupId: string) =>
        store.getState().ungroupFreeformElements(slideId, rowId, columnId, groupId),
      [store],
    ),
    reorderFreeformLayers: useCallback(
      (slideId: string, rowId: string, columnId: string, orderedElementIds: string[]) =>
        store.getState().reorderFreeformLayers(slideId, rowId, columnId, orderedElementIds),
      [store],
    ),
    upsertRevealStep: useCallback(
      (slideId: string, step: RevealStep, index?: number) =>
        store.getState().upsertRevealStep(slideId, step, index),
      [store],
    ),
    removeRevealStep: useCallback(
      (slideId: string, stepId: string) => store.getState().removeRevealStep(slideId, stepId),
      [store],
    ),
    reorderRevealSteps: useCallback(
      (slideId: string, stepIds: string[]) =>
        store.getState().reorderRevealSteps(slideId, stepIds),
      [store],
    ),
    updateBeatMeta: useCallback(
      (slideId: string, patch: Partial<BeatMeta>) =>
        store.getState().updateBeatMeta(slideId, patch),
      [store],
    ),
    updateRouting: useCallback(
      (
        slideId: string,
        patch: Partial<NonNullable<BeatMeta['next']>>,
      ) => store.getState().updateRouting(slideId, patch),
      [store],
    ),
    updateQuizBranch: useCallback(
      (
        slideId: string,
        optionId: string,
        patch: Record<string, unknown>,
      ) => store.getState().updateQuizBranch(slideId, optionId, patch),
      [store],
    ),
    duplicateSelection: useCallback(() => store.getState().duplicateSelection(), [store]),
    deleteSelection: useCallback(() => store.getState().deleteSelection(), [store]),
    copySelection: useCallback(() => store.getState().copySelection(), [store]),
    pasteSelection: useCallback(() => store.getState().pasteSelection(), [store]),
    flush: useCallback(async () => store.getState().flushSync(), [store]),
    drainAndReset: useCallback(async () => store.getState().drainAndReset(), [store]),
  };
}

export const useTemporal = <T,>(selector: (state: ReturnType<typeof useEditorStore.temporal.getState>) => T) =>
  useStore(useEditorStore.temporal, selector);
